<template>
  <div>
    <Breadcrumb :nameNav="nameNav"></Breadcrumb>
    <!-- Table Container Card -->

    <b-card class="m-2" no-body>
      <b-card-header class="pb-50">
        <h5 class="text-capitalize">
          <span v-if="!isUpdating">{{ $t("alarm_views.action_add") }}</span>
          <span v-else>{{ $t("alarm_views.action_update") }}</span>
        </h5>
      </b-card-header>
      <b-card-body>
        <div class="mx-2 mb-2">
          <validation-observer ref="refFormObserver">
            <div class="m-2">
              <!-- ===================================== -->
              <b-row class="mb-2">
                <b-col md="12" class="mb-2">
                  <h3>{{ $t("alarm_views.subtitle_1") }}</h3>
                </b-col>

                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('alarm_views.fields.name.label')"
                    rules="required"
                  >
                    <label class="d-block">{{
                      $t("alarm_views.fields.name.label")
                    }}</label>
                    <b-form-input
                      v-model="viewName"
                      class="d-inline-block mr-1"
                      :placeholder="$t('alarm_views.fields.name.placeholder')"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>

                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    name="Tenant"
                    rules="required"
                  >
                    <label class="d-block" for="tenant">
                      {{ $t("alarm_views.fields.tenant.label") }}
                    </label>
                    <v-select
                      v-model="selectedTenant"
                      :options="tenantOptions"
                      :clearable="false"
                      input-id="tenant"
                      class="w-100"
                      :reduce="(val) => val.value"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>

                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('alarm_views.fields.users.label')"
                    rules="required"
                  >
                    <label class="d-flex align-items-center justify-content-between" style="position: relative">
                      {{ $t("alarm_views.fields.users.label") }}
                      <users-select-filter
                        v-if="usersOptions.length"
                        :user-options="usersOptions"
                        :users-selected="selectedUsers"
                        @users-filtered="usersFilteredOptions = $event"
                        @users-selected="selectedUsers = $event"
                      />
                    </label>
                    <v-select
                      v-model="selectedUsers"
                      :options="usersFilteredOptions.length ? usersFilteredOptions : usersOptions"
                      class="w-100 users-select--scrollable"
                      input-id="users"
                      multiple
                      :reduce="(val) => val.value"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
              </b-row>

              <!-- ===================================== -->
              <b-row class="mb-2">
                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('alarm_views.fields.alarm_level.label')"
                    rules="required"
                  >
                    <label class="d-flex" for="alarm_level">
                      <div class="d-flex flex-fill">
                        {{ $t("alarm_views.fields.alarm_level.label") }}
                      </div>
                      <div class="d-flex justify-content-end flex-fill">
                        <b-form-checkbox
                          :disabled="!alarmLevelOptions.length"
                          @change="isChecked => selectAllAlarmLevels(isChecked)"
                        >
                          {{ $t("alarm_views.btns.select_all") }}
                        </b-form-checkbox>
                      </div>
                    </label>
                    <v-select
                      v-model="selectedAlarmLevel"
                      :options="alarmLevelOptions"
                      class="w-100"
                      input-id="alarm_level"
                      multiple
                      :reduce="(val) => val.value"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>

                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('alarm_views.fields.alarm_type.label')"
                    rules="required"
                  >
                    <label class="d-flex" for="alarm_type">
                      <div class="d-flex flex-fill">
                        {{ $t("alarm_views.fields.alarm_type.label") }}
                      </div>
                      <div class="d-flex justify-content-end flex-fill">
                        <b-form-checkbox
                          :disabled="!alarmTypeOptions.length"
                          @change="isChecked => selectAllAlarmTypes(isChecked)"
                        >
                          {{ $t("alarm_views.btns.select_all") }}
                        </b-form-checkbox>
                      </div>
                    </label>
                    <v-select
                      v-model="selectedAlarmType"
                      :options="alarmTypeOptions"
                      class="w-100"
                      input-id="alarm_type"
                      multiple
                      :reduce="(val) => val.value"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
                <b-col class="mb-1 mb-md-0">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('alarm_views.fields.alarm_status.label')"
                    rules="required"
                  >
                    <label class="d-block" for="alarm_status">
                      {{ $t("alarm_views.fields.alarm_status.label") }}
                    </label>
                    <v-select
                      v-model="selectedAlarmStatus"
                      :options="alarmStatusOptions"
                      class="w-100"
                      input-id="alarm_status"
                      multiple
                      :reduce="(val) => val.value"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </validation-provider>
                </b-col>
              </b-row>

              <!-- ===================================== -->
              <b-row class="mb-2">
                <b-col md="4">
                  <div>
                    <b-form-checkbox
                      id="is_default"
                      name="is_default"
                      v-model="isDefault"
                      class="text-capitalize py-50"
                      switch
                    >
                      {{ $t("alarm_views.fields.default_view.label") }}
                    </b-form-checkbox>
                  </div>
                </b-col>

                <!-- =================== Frequency ================== -->
                <b-col md="4" class="mb-1 mb-md-0">
                  <label class="d-block">
                    {{ $t("alarm_views.fields.periodicity.label") }}
                  </label>
                  <div>
                    <b-form-checkbox
                      id="always"
                      name="always"
                      v-model="always"
                      class="text-capitalize py-50"
                      switch
                    >
                      {{ $t("alarm_views.fields.periodicity.always") }}
                    </b-form-checkbox>
                  </div>
                  <b-tabs content-class="w-75" v-if="!always">
                    <b-tab
                      v-for="(weekday, index) in weekdays"
                      :key="index"
                      :title="$t(`alarm_views.fields.periodicity.days.${weekday.text}`)"
                    >
                      <table class="table table-sm w-75 mb-2">
                        <tr>
                          <th>{{ $t("alarm_views.fields.periodicity.from") }}</th>
                          <th>{{ $t("alarm_views.fields.periodicity.to") }}</th>
                          <th></th>
                        </tr>
                        <tr v-for="(dt, dt_index) in weekday.data" :key="dt_index">
                          <td>
                            <input
                              type="time"
                              v-model="weekday.data[dt_index].start_at"
                            />
                          </td>
                          <td>
                            <input type="time" v-model="weekday.data[dt_index].end_at" />
                          </td>
                          <td>
                            <b-button
                              variant="danger"
                              @click="removeDt(index, dt_index)"
                              size="sm"
                            >
                              {{ $t("alarm_views.fields.periodicity.btns.delete") }}
                            </b-button>
                          </td>
                        </tr>
                      </table>

                      <div class="d-flex justify-content-end">
                        <b-button variant="success" @click="addDt(index)" size="sm">
                          {{ $t("alarm_views.fields.periodicity.btns.add") }}
                        </b-button>
                      </div>
                    </b-tab>

                    <p class="text-danger" v-if="showWeekdaysValidationErrors">
                      {{ $t("alarm_views.fields.periodicity.messages.invalid_schedule") }}
                    </p>
                  </b-tabs>
                </b-col>
              </b-row>
              <hr />
              <!-- ===================================== -->
              <b-row>
                <b-col md="12">
                  <div class="d-flex">
                    <h3 class="d-flex flex-fill">{{ $t("alarm_views.subtitle_2") }}</h3>
                    <div class="d-flex justify-content-end flex-fill">
                      <b-button variant="success" @click="save">
                        <span v-if="!isUpdating">{{
                          $t("alarm_views.btns.create")
                        }}</span>
                        <span v-else>{{ $t("alarm_views.btns.update") }}</span>
                      </b-button>
                    </div>
                  </div>
                </b-col>

                <b-col md="12">
                  <b-tabs no-key-nav>
                    <b-tab
                      v-for="tab in configTabs"
                      :key="tab.id"
                      :active="activeConfigTabId === tab.id"
                      @click.native="setActiveConfigTab(tab.id)"
                    >
                      <template #title>
                        <span>{{ tab.name }}</span>
                        <span
                          v-if="configTabs.length > 1"
                          class="ml-50 cursor-pointer d-inline-flex align-items-center"
                          @click.stop="removeConfigTab(tab.id)"
                        >
                          <feather-icon icon="XIcon" size="14" />
                        </span>
                      </template>
                      <b-row>
                        <b-col md="4" style="height: 70vh; overflow: auto">
                          <slot-options
                            class="mb-2"
                            :tags-options="tagsOptions"
                            :link-options="linkOptions"
                            :filters="filters"
                          />
                          <layouts-widget
                            parent="alarm-views"
                            :selected-layout="tab.id_layout"
                            @layouts-widget-ready="onLayoutsWidgetReady"
                            @layout-selected="onLayoutSelected"
                            :force-wide-layout="true"
                            :collapsible="false"
                          />
                        </b-col>
                        <b-col md="8">
                          <p class="text-danger" v-if="showLayoutItemsValidationErrors">
                            El layout no puede estar completamente vacío
                          </p>
                          <preview-layout
                            v-if="tab.id_layout"
                            :selected-layout="tab.id_layout"
                            :layout-items="tab.layoutItems || []"
                          />
                        </b-col>
                      </b-row>
                    </b-tab>
                    <template #tabs-end>
                      <b-button
                        v-if="canAddConfigTab"
                        variant="outline-primary"
                        size="sm"
                        class="btn-icon ml-1"
                        v-b-tooltip.hover
                        :title="$t('tooltips.add_new_tab')"
                        @click="addConfigTab"
                      >
                        <feather-icon icon="PlusIcon" size="14" />
                      </b-button>
                    </template>
                  </b-tabs>
                </b-col>
              </b-row>
            </div>
          </validation-observer>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormCheckbox,
  BFormInvalidFeedback,
  BTabs,
  BTab,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import useAlarmViewConfig from "./useAlarmViewConfig";
import LayoutsWidget from "../components/GridView/widgets/LayoutsWidget.vue";
import PreviewLayout from "./layout/PreviewLayout.vue";
import SlotOptions from "./widgets/SlotOptions.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import UsersSelectFilter from "@/components/Filters/UsersSelectFilter.vue";

export default {
  components: {
    Breadcrumb,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormCheckbox,
    BFormInvalidFeedback,
    BTabs,
    BTab,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
    LayoutsWidget,
    PreviewLayout,
    SlotOptions,
    UsersSelectFilter,
  },
  setup(_) {
    return { ...useAlarmViewConfig() };
  },
};
</script>

<style lang="scss" scoped>
::v-deep .users-select--scrollable .vs__selected-options {
  max-height: 140px;
  overflow-x: hidden;
  overflow-y: auto;
  align-content: flex-start;
}
</style>
